import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  downloadOfficialDocument,
  errorMessage,
  fetchCompanyDocuments,
  fetchOfficialDocumentOrder,
  fetchOfficialDocuments,
  orderOfficialDocument,
  type CompanyDocuments,
  type OfficialDocumentOption,
  type OfficialDocumentOrder,
} from './api';
import { rememberedCompanyNameForVat } from './activityCompanyMemory';
import { saveDownloadedFile } from './downloadFile';

const currency = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const dateTime = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export function documentPrice(value: number) {
  return currency.format(value / 100);
}

export function officialOrderElementId(orderId: string) {
  return `official-order-${orderId}`;
}

export function OfficialDocumentOrderRow({ initialOrder, showCompany = false }: { initialOrder: OfficialDocumentOrder; showCompany?: boolean }) {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['official-document-order', initialOrder.orderId],
    queryFn: () => fetchOfficialDocumentOrder(initialOrder.orderId),
    initialData: initialOrder,
    staleTime: initialOrder.archiviato ? Infinity : 0,
    refetchInterval: (query) => query.state.data?.archiviato ? false : 18_000,
    refetchIntervalInBackground: false,
  });
  const order = status.data;
  const download = useMutation({
    mutationFn: () => downloadOfficialDocument(order.orderId),
    onSuccess: (file) => {
      saveDownloadedFile(file);
      queryClient.invalidateQueries({ queryKey: ['official-document-order', order.orderId] });
      queryClient.invalidateQueries({ queryKey: ['official-documents'] });
    },
  });
  const rememberedName = showCompany ? rememberedCompanyNameForVat(order.vat) : null;
  const readableStatus = order.ready
    ? order.archiviato ? 'Pronto · archiviato da Osservio' : order.status || 'Pronto'
    : 'In erogazione';

  return <article id={officialOrderElementId(order.orderId)} className={`official-order${order.ready ? ' is-ready' : ''}${order.archiviato ? ' is-archived' : ''}`}>
    <span className="official-order-state" aria-hidden="true">{order.ready ? '✓' : '…'}</span>
    <div className="official-order-copy">
      <strong>{order.label}</strong>
      {showCompany && <span className="official-order-company">{rememberedName ?? 'Società'} · P.IVA {order.vat}</span>}
      <p>{readableStatus}</p>
      <small>Ordinato il {dateTime.format(new Date(order.createdAt))} · pagato {documentPrice(order.priceCents)}</small>
    </div>
    {order.ready
      ? <button type="button" disabled={download.isPending} onClick={() => download.mutate()}>{download.isPending ? 'Preparo…' : 'Scarica'}</button>
      : <span className="official-order-live">Arriva automaticamente</span>}
    {!order.ready && <p className="official-order-wait">In erogazione: arriva da solo, non riordinarlo. Il server controlla lo stato ogni 10 minuti.</p>}
    {order.archiviato && <p className="official-order-kept">Copia conservata da Osservio, scaricabile anche in futuro.</p>}
    {status.isError && <p className="official-order-error">Stato non aggiornabile: {errorMessage(status.error)}</p>}
    {download.isError && <p className="official-order-error">{errorMessage(download.error)} <button type="button" onClick={() => download.mutate()}>Riprova</button></p>}
  </article>;
}

export function OfficialDocumentsWithoutVat() {
  const archive = useQuery({ queryKey: ['official-documents'], queryFn: fetchOfficialDocuments, staleTime: 30_000 });
  const hasOrders = (archive.data?.length ?? 0) > 0;
  const title = archive.isError ? 'Archivio momentaneamente non leggibile' : hasOrders ? 'Hai già documenti in archivio' : 'Associa prima la società corretta';
  const description = archive.isError
    ? 'Puoi comunque aprire l’archivio e riprovare senza effettuare nuovi acquisti.'
    : hasOrders
      ? 'La P.IVA di questa scheda non è ancora confermata. Nell’archivio trovi comunque tutti gli acquisti, ordinati per società.'
      : 'Conferma la P.IVA nel blocco Dati societari per vedere il listino ed evitare acquisti duplicati.';
  return <section className="official-documents official-documents-unlinked local-detail-section" aria-labelledby="official-documents-unlinked-title">
    <header><div><p className="eyebrow">Camera di Commercio</p><h2 id="official-documents-unlinked-title">Documenti ufficiali</h2></div></header>
    {archive.isPending && <div className="official-documents-loading" role="status"><i/><i/><i/><span>Controllo i documenti già acquistati…</span></div>}
    {!archive.isPending && <div className="official-documents-unlinked-copy">
      <span aria-hidden="true">{hasOrders ? '✓' : '○'}</span>
      <div><strong>{title}</strong><p>{description}</p></div>
      <Link className="btn-secondary" to="/documenti">{hasOrders ? 'Apri i miei documenti' : 'Vedi archivio documenti'} <span aria-hidden="true">→</span></Link>
    </div>}
  </section>;
}

export default function OfficialDocuments({ vat }: { vat: string }) {
  const queryClient = useQueryClient();
  const [candidate, setCandidate] = useState<OfficialDocumentOption | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const documents = useQuery({
    queryKey: ['company-documents', vat],
    queryFn: () => fetchCompanyDocuments(vat),
  });
  const purchase = useMutation({
    mutationFn: (document: OfficialDocumentOption) => orderOfficialDocument(vat, document.type),
    onSuccess: (order) => {
      queryClient.setQueryData<CompanyDocuments>(['company-documents', vat], (current) => current ? { ...current, orders: [order, ...current.orders.filter((item) => item.orderId !== order.orderId)] } : current);
      queryClient.setQueryData(['official-document-order', order.orderId], order);
      queryClient.invalidateQueries({ queryKey: ['official-documents'] });
      setCandidate(null);
      setAcknowledged(false);
    },
  });
  const orderedByType = useMemo(() => new Map((documents.data?.orders ?? []).map((order) => [order.type, order])), [documents.data?.orders]);

  useEffect(() => {
    setCandidate(null);
    setAcknowledged(false);
    purchase.reset();
  }, [vat]);

  const revealOrder = (order: OfficialDocumentOrder) => {
    setCandidate(null);
    setAcknowledged(false);
    window.requestAnimationFrame(() => document.getElementById(officialOrderElementId(order.orderId))?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };
  const choose = (document: OfficialDocumentOption) => {
    const existing = orderedByType.get(document.type);
    if (existing) {
      revealOrder(existing);
      return;
    }
    setCandidate(document);
    setAcknowledged(false);
    purchase.reset();
  };
  const cancel = () => {
    setCandidate(null);
    setAcknowledged(false);
    purchase.reset();
  };

  return <section className="official-documents local-detail-section" aria-labelledby="official-documents-title">
    <header><div><p className="eyebrow">Camera di Commercio</p><h2 id="official-documents-title">Documenti ufficiali</h2></div><span className="official-documents-paid">A pagamento</span></header>
    <p className="official-documents-intro">Prezzo e stato restano visibili anche dopo l’ordine. I documenti acquistati sono sempre recuperabili da <Link to="/documenti">I miei documenti</Link>.</p>

    {documents.isPending && <div className="official-documents-loading" role="status"><i/><i/><i/><span>Carico listino e acquisti…</span></div>}
    {documents.isError && <div className="notice notice-error"><p>{errorMessage(documents.error)}</p><button type="button" className="btn-secondary" onClick={() => documents.refetch()}>Riprova</button></div>}
    {documents.data && <>
      {documents.data.available.length > 0 ? <div className="official-documents-list" role="list" aria-label="Documenti ufficiali ordinabili">{documents.data.available.map((document) => {
        const existing = orderedByType.get(document.type);
        return <article key={document.type} className={existing ? 'is-ordered' : undefined} role="listitem"><div><strong>{document.label}</strong><span>{documentPrice(document.priceCents)}</span></div><button type="button" disabled={purchase.isPending} onClick={() => choose(document)}>{existing ? 'Già ordinato · vedi' : 'Ordina'}</button></article>;
      })}</div> : <p className="local-honest-state">Per questa impresa non risultano documenti ordinabili nel listino camerale.</p>}

      {candidate && <section className="official-purchase-confirm" aria-labelledby="official-purchase-title">
        <p className="eyebrow">Conferma acquisto</p>
        <h3 id="official-purchase-title">{candidate.label}</h3>
        <p>Stai acquistando un documento ufficiale della Camera di Commercio: <strong>{documentPrice(candidate.priceCents)} dal wallet</strong>. Confermi?</p>
        <label><input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)}/><span>Ho letto il prezzo e autorizzo l’addebito al wallet.</span></label>
        <div><button type="button" className="btn-quiet" disabled={purchase.isPending} onClick={cancel}>Annulla</button><button type="button" className="btn-danger" disabled={!acknowledged || purchase.isPending} onClick={() => purchase.mutate(candidate)}>{purchase.isPending ? 'Invio l’ordine…' : `Conferma acquisto · ${documentPrice(candidate.priceCents)}`}</button></div>
        {purchase.isError && <p className="official-purchase-error">{errorMessage(purchase.error)}</p>}
      </section>}

      {documents.data.orders.length > 0 && <section className="official-orders" aria-labelledby="official-orders-title"><header><div><p className="eyebrow">Acquisti della società</p><h3 id="official-orders-title">I documenti già ordinati</h3></div><span>{documents.data.orders.length}</span></header><div>{documents.data.orders.map((order) => <OfficialDocumentOrderRow key={order.orderId} initialOrder={order}/>)}</div></section>}
    </>}
  </section>;
}
